import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Loader2, Check, ArrowRight } from "lucide-react";
import { api, apiError, track } from "@/lib/api";
import { AssessmentStep } from "@/components/AssessmentSteps";
import { draftToken, draftConfig, readDraft } from "@/lib/assessment";
import { toast } from "sonner";

export default function Assessment() {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [stage, setStage] = useState("questions");
  const [creativeText, setCreativeText] = useState("");
  const [background, setBackground] = useState({});
  const [educationOptions, setEducationOptions] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const cheer = idx >= 5 ? "No right or wrong answers. Just your way of seeing things." : "Go with what feels like you — not what sounds impressive.";

  useEffect(() => {
    if (!draftToken()) { navigate("/onboarding", { replace: true }); return; }
    track("assessment_started");
    Promise.all([api.get("/assessment/questions"), readDraft()]).then(([q, d]) => {
      if (d.data.stage === "revealed") { navigate("/result", { replace: true }); return; }
      setQuestions(q.data.questions); setEducationOptions(q.data.education_options);
      setName(d.data.first_name); setIdx(d.data.idx); setStage(d.data.stage);
      setAnswers(Object.fromEntries(Object.entries(d.data.responses).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])));
      setCreativeText(d.data.creative_text); setBackground(d.data.background);
    }).catch(e => setError(apiError(e)));
  }, [navigate]);

  if (error) return <div className="mx-auto max-w-xl px-5 py-24" data-testid="assessment-error"><p>{error}</p><button data-testid="assessment-restart" className="mt-4 font-bold underline" onClick={() => navigate("/onboarding")}>Start again with your first name</button></div>;

  if (!questions.length) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  }

  const q = questions[idx];
  const total = questions.length;
  const picked = answers[q.id] || [];
  const progress = ((idx + (picked.length ? 1 : 0)) / total) * 100;

  const toggle = (oid) => setAnswers({ ...answers, [q.id]: [oid] });
  const payload = (bg = background) => ({ responses: answers, creative_text: creativeText, background: bg });
  const moveTo = async (nextIdx, nextStage = "questions") => {
    setBusy(true);
    try {
      await api.put("/assessment/draft", { ...payload(), idx: nextIdx, stage: nextStage }, draftConfig());
      setIdx(nextIdx); setStage(nextStage); window.scrollTo(0, 0);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const reveal = async (bg) => {
    setBusy(true);
    try {
      await api.post("/assessment/draft/reveal", payload(bg), draftConfig());
      sessionStorage.setItem("nxt_assessment_revealed", "1");
      navigate("/result");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const submit = () => {
    if (!picked.length || (q.open_text && !creativeText.trim())) return;
    track("assessment_question_answered", { q: q.id });
    if (idx === 4) moveTo(5, "puzzle_intro");
    else if (idx === 9) moveTo(9, "reflection");
    else moveTo(idx + 1);
  };

  if (stage !== "questions") return <AssessmentStep key={stage} stage={stage} name={name} background={background} educationOptions={educationOptions} busy={busy}
    onContinue={stage === "background" ? reveal : () => moveTo(stage === "puzzle_intro" ? 5 : 9, stage === "puzzle_intro" ? "questions" : "background")}
    onBack={() => moveTo(stage === "puzzle_intro" ? 4 : 9, stage === "background" ? "reflection" : "questions")} />;

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-xl items-center gap-4 px-5 py-4">
          <button disabled={busy} aria-label="Previous prompt" onClick={() => (idx > 0 ? moveTo(idx - 1) : navigate("/onboarding"))}
            data-testid="assessment-back-btn" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
            <motion.div className="h-full rounded-full bg-indigo-600" animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }} />
          </div>
          <span className="text-right text-xs font-bold text-muted-foreground" data-testid="assessment-progress">
            {name}'s Career DNA · {idx < 5 ? "Part A" : "Part B"} · {idx % 5 + 1}/5
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-xl px-5 py-8">
          <AnimatePresence mode="wait">
            <motion.div key={q.id}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">{q.section}</div>
              {cheer && <div className="mt-1 text-sm font-semibold text-emerald-600" data-testid="assessment-cheer">{cheer}</div>}
              <h1 data-testid="assessment-question" className="mt-3 font-display text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl text-balance">
                {q.text}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground" data-testid="assessment-hint">{q.intro || "Which feels closest to you? There isn't an answer you're supposed to give."}</p>
              {q.open_text && <div className="mt-5"><label htmlFor="creative-idea" className="text-sm font-bold">Your idea, in your own words</label><textarea id="creative-idea" data-testid="assessment-creative-input" value={creativeText} onChange={e => setCreativeText(e.target.value)} maxLength={1000} rows={3} placeholder="What if we…" className="mt-2 w-full rounded-2xl border border-input bg-card p-4 outline-none focus:ring-2 focus:ring-indigo-500" /><p className="mt-1 text-xs text-muted-foreground">No word count to aim for. Your idea is saved for reflection; the approach below supplies the thinking-style signal.</p></div>}
              <div className="mt-6 space-y-3">
                {q.options.map((o, i) => {
                  const selected = picked.includes(o.id);
                  return (
                    <motion.button key={o.id}
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      onClick={() => toggle(o.id)} disabled={busy} data-testid={`answer-${q.id}-${o.id}`} aria-pressed={selected}
                      className={`group flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all active:scale-[0.99] ${
                        selected ? "border-indigo-600 bg-indigo-50" : "border-border bg-card hover:border-indigo-300 hover:bg-secondary/50"
                      }`}>
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-2 font-display font-bold transition-colors ${
                        selected ? "border-indigo-600 bg-indigo-600 text-white" : "border-border text-muted-foreground group-hover:border-indigo-400"
                      }`}>
                        {selected ? <Check className="h-5 w-5" /> : String.fromCharCode(65 + i)}
                      </span>
                      <span className="font-semibold leading-snug">{o.label}</span>
                    </motion.button>
                  );
                })}
              </div>
              {q.part === "B" && picked.length > 0 && <p aria-live="polite" data-testid="puzzle-positive-insight" className="mt-4 rounded-2xl bg-emerald-50 p-4 text-sm font-medium text-emerald-800">{q.options.find(o => o.id === picked[0])?.insight}</p>}
              <button onClick={submit} disabled={busy || !picked.length || (q.open_text && !creativeText.trim())} data-testid="assessment-submit-btn"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-4 font-bold text-white transition-all hover:bg-indigo-600 active:scale-[0.99] disabled:opacity-40">
                {busy ? "Saving your progress…" : idx < total - 1 ? "Continue" : "See what connects it all"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="assessment-picked">{picked.length ? "Your choice is yours to change. Progress saves when you continue." : "Take your time. Choose what feels like you."}</p>
            </motion.div>
          </AnimatePresence>
      </div>
    </div>
  );
}
